import React from 'react';
import { Link } from 'react-router-dom';

const INDEX_NAMES = {
  CBA00101_1Y: '中债新综合1年以下',
  CBA00101: '中债新综合',
  CBA_COMPOSITE: '中债综合',
  GOVT_BOND: '国债总指数',
  CREDIT_BOND: '信用债总指数',
  NCD: '同业存单总指数',
  CSI300: '沪深300',
  CSI500: '中证500',
  CSI800: '中证800',
  CSI1000: '中证1000',
  STAR100: '科创100',
  SSE50: '上证50',
  CHINEXT: '创业板',
  CSI_CONVERT: '中证转债'
};

function fmtPct(value, digits = 3) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `${number > 0 ? '+' : ''}${number.toFixed(digits)}%`;
}

function tone(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return 'neutral';
  return number > 0 ? 'positive' : 'negative';
}

function fmtRate(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `${number.toFixed(4)}%`;
}

function fmtBp(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `${number > 0 ? '+' : ''}${number.toFixed(2)}bp`;
}

function RateIndicatorCard({ indicator }) {
  const value = indicator.value || {};
  const isSpread = indicator.key === 'credit_spread';
  const primaryText = isSpread ? fmtBp(value.value_bp) : fmtRate(value.value_pct);
  return (
    <article className={`market-rate-card tone-${indicator.tone || 'unknown'}`}>
      <div className="market-rate-head">
        <div><span>{indicator.label}</span><small>{indicator.metric_label}</small></div>
        <strong>{primaryText}</strong>
      </div>
      {indicator.curve?.length ? (
        <div className="market-rate-curve">
          {indicator.curve.map((point) => (
            <div key={point.tenor}><span>{point.tenor}</span><strong>{fmtRate(point.value_pct)}</strong></div>
          ))}
        </div>
      ) : (
        <div className="market-rate-changes">
          <div><span>1日变化</span><strong>{fmtBp(value.change_1d_bp)}</strong></div>
          <div><span>5日变化</span><strong>{fmtBp(value.change_5d_bp)}</strong></div>
          <div><span>20日变化</span><strong>{fmtBp(value.change_20d_bp)}</strong></div>
        </div>
      )}
      {indicator.key === 'government_curve' && indicator.curve_slope_bp != null ? (
        <div className="market-curve-shape">
          <div className="market-curve-shape-head"><span>{indicator.curve_shape?.label || '期限利差'}</span><small>{indicator.curve_shape?.detail}</small></div>
          <div className="market-curve-spreads">{(indicator.curve_spreads || []).filter((item) => item.available).map((item) => (
            <div key={item.key}><span>{item.label}</span><strong>{fmtBp(item.value_bp)}</strong><small>1日 {fmtBp(item.change_1d_bp)} · 5日 {fmtBp(item.change_5d_bp)} · 20日 {fmtBp(item.change_20d_bp)}</small></div>
          ))}</div>
        </div>
      ) : null}
      {indicator.key === 'credit_spread' && indicator.credit_layers?.length ? (
        <div className="market-credit-layers">
          <div className="market-curve-shape-head"><span>{indicator.credit_state?.label || '信用分层'}</span><small>3bp仅作环境观察边界，不是买卖阈值</small></div>
          <div className="market-credit-layer-grid">
            {indicator.credit_layers.map((item) => (
              <div key={item.rating}>
                <span>{item.rating} · 3Y中票</span>
                <strong>{fmtRate(item.yield?.value_pct)}</strong>
                <small>对3Y国债利差 {fmtBp(item.spread?.value_bp)}</small>
                <small>1日 {fmtBp(item.spread?.change_1d_bp)} · 5日 {fmtBp(item.spread?.change_5d_bp)} · 20日 {fmtBp(item.spread?.change_20d_bp)}</small>
              </div>
            ))}
          </div>
          {indicator.rating_premium?.available ? (
            <div className="market-rating-premium">
              <span>{indicator.rating_premium.label}</span><strong>{fmtBp(indicator.rating_premium.value_bp)}</strong>
              <small>1日 {fmtBp(indicator.rating_premium.change_1d_bp)} · 5日 {fmtBp(indicator.rating_premium.change_5d_bp)} · 20日 {fmtBp(indicator.rating_premium.change_20d_bp)}</small>
            </div>
          ) : null}
        </div>
      ) : null}
      <p>{indicator.conclusion}</p>
      <div className="market-factor-use"><span>怎么用</span>{indicator.use}</div>
      <div className="market-factor-foot">数据日 {value.trade_date || '—'} · 历史点 {value.history_points || 0}</div>
    </article>
  );
}

function signalLabel(signal) {
  if (signal === 'tightening') return '偏紧';
  if (signal === 'easing') return '偏松';
  if (signal === 'neutral') return '平稳';
  return '不足';
}

export function LiquidityRegimePanel({ regime, compact = false }) {
  if (!regime?.available || (compact && regime.selected_for_product === false)) return null;
  return (
    <section className={compact ? 'market-liquidity-regime compact' : 'market-liquidity-regime'}>
      <div className="market-liquidity-head">
        <div><span>{regime.state?.label || '短端流动性环境'}</span><small>{regime.detail}</small></div>
        {compact ? <em>{regime.relevance?.label || '相关'}</em> : <em>数据日 {regime.as_of || '—'}</em>}
      </div>
      <div className="market-liquidity-signals">
        {(regime.signals || []).map((item) => (
          <div key={item.key}>
            <span>{item.label}</span><strong>{item.key === 'ncd_index' ? fmtPct(item.change_5d) : fmtBp(item.change_5d)}</strong><em className={`liquidity-${item.signal}`}>{signalLabel(item.signal)}</em>
          </div>
        ))}
      </div>
      {!compact ? <><div className="market-factor-use"><span>怎么用</span>资金利率上行会增加杠杆融资和短债估值压力；存单收益率与财富指数共同确认银行负债端和短端资产价格。信号分化时不应只凭单项指标下结论。</div><small className="market-liquidity-note">{regime.observation_note}</small></> : <p>与本产品：{regime.relevance?.reason || '短端资产或融资端相关'}</p>}
    </section>
  );
}

export function MarketRateIndicatorPanel({ rateIndicators, compact = false }) {
  const indicators = rateIndicators?.indicators || [];
  if (!indicators.length) return null;
  if (compact) {
    const selected = indicators.filter((item) => item.selected_for_product !== false && !['funding_rate', 'ncd_yield'].includes(item.key));
    const liquiditySelected = rateIndicators.liquidity_regime?.available && rateIndicators.liquidity_regime?.selected_for_product !== false;
    if (!selected.length && !liquiditySelected) return null;
    return (
      <section className="product-market-refs">
        <div className="market-rate-title">
          <div><span>本产品引用的公共市场指标</span><small>完整市场数据统一维护在“宏观环境”；这里仅保留相关指标及匹配原因</small></div>
          <Link to="/macro?tab=bond">查看完整市场环境</Link>
        </div>
        <LiquidityRegimePanel regime={rateIndicators.liquidity_regime} compact />
        <div className="product-market-ref-list">
          {selected.map((indicator) => {
            const value = indicator.value || {};
            const current = indicator.key === 'credit_spread'
              ? fmtBp(value.value_bp)
              : fmtRate(value.value_pct);
            return (
              <div key={indicator.key}>
                <span>{indicator.label}<small>{indicator.metric_label}</small></span>
                <strong>{current}</strong>
                <em>{indicator.relevance?.label || '相关'}</em>
                <p>{indicator.conclusion}<small>{indicator.relevance?.reason || ''}</small></p>
              </div>
            );
          })}
        </div>
      </section>
    );
  }
  return (
    <section className="market-rate-section">
      <div className="market-rate-title">
        <div><span>利率、资金面与信用利差</span><small>全市场公共数据：收益率看方向，利差看信用风险补偿</small></div>
        <em>数据日 {rateIndicators.as_of || '—'}</em>
      </div>
      <LiquidityRegimePanel regime={rateIndicators.liquidity_regime} />
      <div className="market-rate-grid">
        {indicators.filter((item) => !['funding_rate', 'ncd_yield'].includes(item.key)).map((indicator) => (
          <RateIndicatorCard indicator={indicator} key={indicator.key} />
        ))}
      </div>
      <p className="decision-muted market-factor-note">{rateIndicators.source}</p>
    </section>
  );
}

function FactorCard({ group }) {
  const primary = group.primary;
  return (
    <article className={`market-factor-card tone-${group.tone || 'unknown'}`}>
      <div className="market-factor-head">
        <div><span>{group.label}</span><small>{primary ? (INDEX_NAMES[primary.code] || primary.code) : '暂无行情'}</small></div>
        <em className={`relevance-${group.relevance?.level || 'watch'}`}>{group.relevance?.label || '观察项'}</em>
      </div>
      <div className="market-factor-metrics">
        <div><span>当日</span><strong className={`tone-${tone(primary?.change_pct)}`}>{fmtPct(primary?.change_pct)}</strong></div>
        <div><span>近5日</span><strong className={`tone-${tone(primary?.change_5d_pct)}`}>{fmtPct(primary?.change_5d_pct)}</strong></div>
        <div><span>近20日</span><strong className={`tone-${tone(primary?.change_20d_pct)}`}>{fmtPct(primary?.change_20d_pct)}</strong></div>
      </div>
      {group.equity_regime?.available ? <EquityRegimeSummary regime={group.equity_regime} compact /> : null}
      <p className="market-factor-conclusion">{group.conclusion}</p>
      <div className="market-factor-relevance">与本产品：{group.relevance?.reason || '暂未建立明确关联'}</div>
      <div className="market-factor-use"><span>怎么用</span>{group.use}</div>
      <div className="market-factor-foot">数据日 {primary?.trade_date || '—'} · 历史 {primary?.history_points || 0} 日</div>
    </article>
  );
}

function fmtRelative(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `${number > 0 ? '+' : ''}${number.toFixed(2)}个百分点`;
}

export function EquityRegimeSummary({ regime, compact = false }) {
  if (!regime?.available) return null;
  const breadth = regime.breadth || {};
  const styles = regime.styles || {};
  return (
    <section className={compact ? 'market-equity-regime compact' : 'market-equity-regime'}>
      <div className="market-equity-regime-head">
        <div><span>{regime.state?.label || '权益风险偏好'}</span><small>{regime.state?.detail}</small></div>
        <em>数据日 {regime.as_of || '—'}</em>
      </div>
      <div className="market-equity-breadth">
        <div><span>当日上涨宽度</span><strong>{breadth.today?.total ? `${breadth.today.positive}/${breadth.today.total}` : '—'}</strong></div>
        <div><span>近5日上涨宽度</span><strong>{breadth.d5?.total ? `${breadth.d5.positive}/${breadth.d5.total}` : '—'}</strong></div>
        <div><span>近5个观测日</span><strong>{regime.persistence?.majority_up_days || 0}强 / {regime.persistence?.majority_down_days || 0}弱</strong></div>
        <div><span>中小盘−大盘（5日）</span><strong>{fmtRelative(styles.small_vs_large_5d_pct)}</strong></div>
        <div><span>成长−宽基（5日）</span><strong>{fmtRelative(styles.growth_vs_broad_5d_pct)}</strong></div>
      </div>
      {!compact ? <><p>{regime.state?.style_detail}</p><div className="market-factor-use"><span>怎么用</span>宽度和连续性判断上涨或下跌是否普遍，风格差值判断资金偏向大盘、中小盘还是成长；只有产品允许或实际持有权益时才影响产品解释。</div><small className="market-equity-note">{regime.observation_note}</small></> : null}
    </section>
  );
}

function fmtPlainPct(value, digits = 3) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `${number.toFixed(digits)}%`;
}

export function MarketStressPanel({ stress, segmentKey, compact = false }) {
  const segments = (stress?.segments || []).filter((segment) => segment.available)
    .filter((segment) => segmentKey ? segment.key === segmentKey : !compact || segment.selected_for_product !== false);
  if (!segments.length) return null;
  if (compact) {
    return (
      <section className="market-stress-panel compact">
        <div className="market-stress-title"><span>相关市场的波动与回撤</span><small>回撤使用全部已入库历史，波动窗口为7/14/30日</small></div>
        <div className="market-stress-compact-grid">
          {segments.map((segment) => (
            <div key={segment.key}>
              <header><span>{segment.label}</span><em>{segment.relevance?.label || '相关'}</em></header>
              <strong>{segment.state?.label}</strong>
              <p>平均当前回撤 {fmtPlainPct(segment.average_current_drawdown_pct)} · 平均修复 {fmtPlainPct(segment.average_repair_pct, 1)}</p>
              <small>{segment.volatility_state?.label}；{segment.relevance?.reason}</small>
            </div>
          ))}
        </div>
      </section>
    );
  }
  return (
    <section className="market-stress-panel">
      <div className="market-stress-title"><span>市场波动与回撤环境</span><small>{stress.note}</small></div>
      {segments.map((segment) => (
        <article className="market-stress-segment" key={segment.key}>
          <div className="market-stress-segment-head">
            <div><span>{segment.label}</span><strong>{segment.conclusion}</strong></div>
            <em>平均当前回撤 {fmtPlainPct(segment.average_current_drawdown_pct)}</em>
          </div>
          <div className="market-stress-table-scroll">
            <table className="market-stress-table">
              <thead><tr><th>指数</th><th>当前回撤</th><th>历史最大回撤</th><th>谷底修复</th><th>7日波动</th><th>14日波动</th><th>30日波动</th><th>全部样本</th></tr></thead>
              <tbody>{(segment.indexes || []).map((item) => (
                <tr key={item.code}>
                  <td><strong>{item.label}</strong><small>{item.code}</small></td>
                  <td>{fmtPlainPct(item.current_drawdown_pct)}</td>
                  <td>{fmtPlainPct(item.full_history_max_drawdown_pct)}</td>
                  <td>{fmtPlainPct(item.repair_pct, 1)}</td>
                  <td>{fmtPlainPct(item.volatility_7d_pct, 3)}</td>
                  <td>{fmtPlainPct(item.volatility_14d_pct, 3)}</td>
                  <td>{fmtPlainPct(item.volatility_30d_pct, 3)}</td>
                  <td>{item.sample_size}点<small>{item.first_date}起</small></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </article>
      ))}
    </section>
  );
}

export default function MarketFactorPanel({ context }) {
  if (!context?.groups?.length) return <p className="decision-muted">暂无市场因子数据。</p>;
  const relevantGroups = context.groups.filter((group) => group.relevance?.level !== 'watch');
  const omittedCount = context.groups.length - relevantGroups.length;
  return (
    <div className="market-factor-panel">
      <div className="market-factor-summary">
        <div><span>市场—产品匹配结论</span><strong>{context.summary}</strong></div>
        <small>行情日期 {context.as_of || '—'}</small>
      </div>
      <div className="market-factor-grid">
        {relevantGroups.map((group) => <FactorCard group={group} key={group.key} />)}
      </div>
      <MarketStressPanel stress={context.market_stress} compact />
      {omittedCount > 0 ? <p className="decision-muted market-factor-note">另有 {omittedCount} 个暂不相关的市场观察项，仅在宏观环境展示。</p> : null}
      <MarketRateIndicatorPanel rateIndicators={context.rate_indicators} compact />
      <p className="decision-muted market-factor-note">{context.data_note}</p>
    </div>
  );
}
